/**
 * Project progress figures from reviewed line status: the "km flown" basis (MB, decision 32), and planned, remaining
 * and complete km.
 *
 * Km flown (decision 32, Q95, 18 Oct 2026; decision 176, Q154, 19 Oct 2026): a fully flown line (the Zdas log says
 * `[Comp 100%]`) counts at its planned length from the survey lines file, because the recorded line starts just before
 * and ends just after the planned line; a partly flown line (`[Part …]`) counts at the distance from the flight log.
 * This is the proposal of `proposeAcceptance`, so the figure is the status's `proposed_m`, whatever the review later
 * decides. One exception: a fully flown line logged more than `OVERFLY_RUN_PAST_LIMIT_M` past its planned length
 * (300790 on F26048: 12.82 km logged for an 8.62 km line, about 4.2 km past its east end) counts at its logged length,
 * so the difference shows as KM Overfly. The `.tjf` import stores TJR's own flown figure in `proposed_m` and has no
 * marker, so imported jobs keep TJR's numbers. The Job Summary's KM Overfly (`kmOverfly` via `tjrFromTeslyn`) is on
 * this basis too (issue #11): TJR's overfly is its km flown less the planned length. `flown_m` (the logged length) is
 * shown in Line review only.
 */
import { parseLineNumber } from './lines.js';
import type { LineStatus, PlannedLine } from './model.js';
import { plannedLineRole } from './reflights.js';
import { isFullyFlown } from './zdas.js';

/**
 * How far past its planned length a fully flown line may be logged before the difference counts as overfly: 300 m
 * (decision 197, Q171; decision 176 was first built with 1 km). Zdas logs a complete line from just before its start
 * to just after its end: 0.02 to 0.13 km more than planned on F26048 (585 complete lines of flights 1 to 13), which
 * is not overfly. 300790's 4.2 km is.
 */
export const OVERFLY_RUN_PAST_LIMIT_M = 300;

/** Metres a line status contributes to "km flown" (daily entry, Daily Log, Job Summary, overview). */
export function flownCreditM(status: Pick<LineStatus, 'log_completion' | 'flown_m' | 'proposed_m'>): number {
  if (isFullyFlown(status.log_completion) && status.flown_m - status.proposed_m > OVERFLY_RUN_PAST_LIMIT_M)
    return status.flown_m;
  return status.proposed_m;
}

/** "Km flown" of a set of statuses, in metres (deleted statuses ignored). */
export function flownCreditTotalM(statuses: readonly LineStatus[]): number {
  return statuses.filter((s) => s.deleted_at === null).reduce((a, s) => a + flownCreditM(s), 0);
}

/**
 * Where an original planned line stands (decision 29; drawn by the lines-remaining map, see the spec's "Reflights
 * and patches"):
 * - `not_flown`: no status yet;
 * - `awaiting_review`: flown, a status still pending, nothing accepted;
 * - `accepted`: accepted in full (the line, a full reflight, or the latest patch);
 * - `reflight_planned` / `patch_planned`: rejected or part-accepted, its reflight or patch planned and not flown yet;
 * - `rejected` / `partial`: reviewed so, with no reflight or patch planned (e.g. a TJR import).
 */
export type LineState =
  | 'not_flown'
  | 'awaiting_review'
  | 'accepted'
  | 'reflight_planned'
  | 'patch_planned'
  | 'rejected'
  | 'partial';

export interface LineProgress {
  /** The original (version 0) planned line. */
  readonly original: PlannedLine;
  readonly state: LineState;
  /** The latest reflight or patch of the line that is not accepted yet (flown or not), else null. */
  readonly open: PlannedLine | null;
  /** Planned metres still to fly: 0 when accepted, the open patch's length, else the whole line. */
  readonly remaining_m: number;
}

export interface PlanProgress {
  /** Σ length of the original lines: reflights and patches never add to it. */
  readonly totalPlannedM: number;
  /** Σ `remaining_m` of the original lines ("Est KM Remaining"). */
  readonly remainingM: number;
  readonly lines: readonly LineProgress[];
}

/**
 * Planned and remaining km from the plan and the reviewed line status (decision 29). Each original line counts once
 * at its planned length; a line is done when a status of the line or a full reflight is accepted, or when its latest
 * patch is accepted. A line whose latest version is a patch not yet accepted counts the patch length; any other line
 * not accepted in full counts its whole length. On F26048 (every rejected line reflown and accepted) this equals
 * TJR's "Est KM Remaining" on both printed reports.
 */
export function planProgress(statuses: readonly LineStatus[], plan: readonly PlannedLine[]): PlanProgress {
  const live = plan.filter((p) => p.deleted_at === null);
  const byId = new Map(live.map((p) => [p.id, p]));
  const byNumber = new Map(live.map((p) => [p.number, p]));
  const family = new Map<number, PlannedLine[]>();
  for (const p of live) {
    if (plannedLineRole(p) === 'original') continue;
    const base = parseLineNumber(p.number).base;
    family.set(base, [...(family.get(base) ?? []), p]);
  }
  const statusesOf = new Map<number, LineStatus[]>();
  for (const s of statuses) {
    if (s.deleted_at !== null) continue;
    const parsed = parseLineNumber(s.line_number);
    if (!parsed.production) continue;
    statusesOf.set(parsed.base, [...(statusesOf.get(parsed.base) ?? []), s]);
  }
  const lineOf = (s: LineStatus): PlannedLine | undefined =>
    byNumber.get(s.line_number) ?? byId.get(s.planned_line_id);

  const lines: LineProgress[] = live
    .filter((p) => plannedLineRole(p) === 'original')
    .map((original) => {
      const mine = statusesOf.get(original.number) ?? [];
      const later = (family.get(original.number) ?? []).sort((a, b) => a.number - b.number);
      const latest = later[later.length - 1];
      const flownOn = (p: PlannedLine): LineStatus[] => mine.filter((s) => s.line_number === p.number);
      const accepted = (s: LineStatus): boolean => s.acceptance === 'accepted';
      const fullAccepted = mine.some(
        (s) => accepted(s) && plannedLineRole(lineOf(s) ?? original) !== 'patch',
      );
      const patchDone =
        latest !== undefined && plannedLineRole(latest) === 'patch' && flownOn(latest).some(accepted);
      if (fullAccepted || patchDone) return { original, state: 'accepted', open: null, remaining_m: 0 };
      // the latest reflight or patch, unless a later version than it has been flown (unplanned in the plan)
      const lastFlown = Math.max(0, ...mine.map((s) => s.line_number));
      const openLine = latest !== undefined && latest.number >= lastFlown ? latest : null;
      const latestIsPatch = openLine !== null && plannedLineRole(openLine) === 'patch';
      const remaining_m = latestIsPatch ? openLine.length_m : original.length_m;
      let state: LineState;
      if (openLine !== null && flownOn(openLine).length === 0) {
        state = latestIsPatch ? 'patch_planned' : 'reflight_planned';
      } else if (mine.length === 0) state = 'not_flown';
      else if (mine.some((s) => s.acceptance === 'pending')) state = 'awaiting_review';
      else {
        const last = [...mine].sort((a, b) => a.line_number - b.line_number).at(-1)!;
        state = last.acceptance === 'partial' ? 'partial' : 'rejected';
      }
      return { original, state, open: openLine, remaining_m };
    });
  return {
    totalPlannedM: lines.reduce((a, l) => a + l.original.length_m, 0),
    remainingM: lines.reduce((a, l) => a + l.remaining_m, 0),
    lines,
  };
}
